import {
  LinkIcon,
  RefreshIcon,
  StarIcon,
  TagIcon,
  TicketIcon,
} from "@heroicons/react/outline";
import { HttpError, useOne, useTranslation } from "@refinedev/core";
import { Calendar, Location } from "components/customIcons";
import Dropzone from "components/dropzone";
import Input from "components/input";
import { Label } from "components/label";
import Select from "components/select";
import {
  IBuildEventPageForm,
  IEventDetail,
  PriceType,
  SupportedLanguage,
  UserType,
} from "interfaces";
import { get } from "lodash";
import moment from "moment";
import { useEffect, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { Link } from "react-router-dom";
import {
  COUNTRY_OPTIONS,
  CURRENCIES,
  PRICE_TYPES,
  PROVINCE,
  SPECIAL_TYPES,
  TIMEZONES,
} from "utills/constants";
import {
  _generateSlug,
  canAccessByType,
  formatNumber,
  isOpenRegistration,
} from "utills/helpers";
import CONFIG from "app-config/config.json";
import DateTimePicker from "components/datetimePicker";
import RadioButton from "components/radioButton";
import { endpointService } from "components/shared/services/endpointService";

interface EventInfoProps {
  isEdit: boolean;
  id: string | null;
  eventDetailData: IEventDetail | null;
}

export const EventInfo = ({
  isEdit,
  id,
  eventDetailData,
}: EventInfoProps) => {
  const {
    control,
    register,
    getFieldState,
    getValues,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<IBuildEventPageForm>(); // retrieve all hook methods
  const { translate, getLocale } = useTranslation();
  const [trigger, setTrigger] = useState(false);

  const { data: categoryData, isLoading: isLoadingCategories } = useOne<
    any[],
    HttpError
  >({
    resource: endpointService.getEventEndpoint("categories"),
    id: "",
    meta: {
      resource: "data",
      filters: [
        {
          field: "category_type",
          operator: "eq",
          value: "sports",
        },
      ],
    },
    queryOptions: {
      enabled: !!(trigger || id),
    } as any,
  });

  const _renderGenerateSlugButton = () => {
    return (getValues("languages").includes(SupportedLanguage.EN) ||
      getValues("languages").includes(SupportedLanguage.VI)) &&
      (getValues("multilingual_content")?.name?.en ||
        getValues("multilingual_content")?.name?.vi) ? (
      <div className="w-8 h-full flex items-center justify-center absolute top-1/2 right-1 -translate-y-1/2 cursor-pointer">
        <RefreshIcon
          className="w-5 text-brand-500"
          onClick={() =>
            setValue(
              "event_info.event_slug",
              _generateSlug(
                getValues(`multilingual_content.name.en`) ||
                  getValues(`multilingual_content.name.vi`)
              ),
              { shouldValidate: true }
            )
          }
        />
      </div>
    ) : (
      <></>
    );
  };

  const provinceIds = PROVINCE.map((proinvce) => ({
    value: proinvce.value,
    label: get(proinvce, `name_${getLocale()}`, ""),
  }));

  const categoryIds = (categoryData?.data ?? []).map((category) => ({
    value: category.id,
    label: get(category, `name_${getLocale()}`, ""),
  }));

  const timezoneArr = Object.keys(TIMEZONES as any).map(
    (key: string, index: number) => ({
      value: key,
      label: get(TIMEZONES, `${key}.value`, ""),
    })
  );

  useEffect(() => {
    if (isEdit && !trigger) {
      setTrigger(true);
    }
  }, [isEdit, trigger]);

  return (
    <div className="box-container p-4 sm:p-6">
      {!isEdit ? (
        (!getFieldState("event_info").isTouched || errors["event_info"]) &&
        !id ? (
          <p>{translate("eventSetup.eventInfo.addEventInfo.note")}</p>
        ) : (
          <div className="flex flex-col">
            {getValues("event_info.banner_url") ? (
              <div className="relative w-full overflow-hidden mb-4">
                <img
                  src={getValues("event_info.banner_url")}
                  alt={`${getValues("event_info.banner_url")} Banner`}
                  className="rounded-lg"
                />
              </div>
            ) : (
              <></>
            )}
            <div className="flex flex-col justify-center items-start">
              <div className="w-full flex flex-row items-start gap-x-2">
                <div className="w-44 shrink-0 flex flex-row items-center gap-x-2">
                  <Calendar />
                  {translate("eventSetup.eventInfo.addEventInfo.eventDateTime")}
                  :
                </div>
                <p>
                  {moment(getValues("event_info.start_date")).format(
                    "HH:mm DD/MM/YYYY"
                  )}{" "}
                  -{" "}
                  {moment(getValues("event_info.end_date")).format(
                    "HH:mm DD/MM/YYYY"
                  )}{" "}
                  <span className="mx-2">•</span>
                  {
                    timezoneArr.find(
                      (timezone) =>
                        timezone.value === getValues("event_info.timezone")
                    )?.label
                  }{" "}
                  <span className="mx-2">•</span>{" "}
                  {
                    CURRENCIES.find(
                      (currency) =>
                        currency.value === getValues("event_info.currency")
                    )?.label
                  }
                </p>
              </div>
              <div className="w-full flex flex-row items-start gap-x-2 mt-2">
                <div className="w-44 shrink-0 flex flex-row items-center gap-x-2">
                  <Location />
                  {translate("eventSetup.eventInfo.addEventInfo.eventVenue")}:
                </div>
                <p>
                  {
                    provinceIds.find(
                      (province) =>
                        province.value === getValues("event_info.venue")
                    )?.label
                  }
                  {getValues("event_info.venue") ? "," : ""}{" "}
                  {getValues("event_info.country")}
                </p>
              </div>
              <div className="w-full flex flex-row items-start gap-x-2 mt-2">
                <div className="w-44 shrink-0 flex flex-row items-center gap-x-2">
                  <LinkIcon className="w-4 h-4" />
                  {translate("eventSetup.eventInfo.addEventInfo.eventLink")}:
                </div>
                <Link
                  to={`${CONFIG.DOMAIN}/event/${getValues(
                    "event_info.event_slug"
                  )}`}
                  target="_blank"
                  className="hover:underline text-brand-500"
                  style={{ wordBreak: "break-word" }}
                >
                  {CONFIG.DOMAIN}/event/{getValues("event_info.event_slug")}
                </Link>
              </div>
              <div className="w-full flex flex-row items-start gap-x-2 mt-2">
                <div className="w-44 shrink-0 flex flex-row items-center gap-x-2">
                  <StarIcon className="w-4 h-4" />
                  {translate(
                    "eventSetup.eventInfo.addEventInfo.eventCategories"
                  )}
                  :
                </div>
                <div
                  className="flex flex-row items-center flex-wrap gap-x-2"
                  style={{ wordBreak: "break-word" }}
                >
                  {categoryIds
                    .filter((category) =>
                      getValues("event_info").event_category_ids.includes(
                        category.value
                      )
                    )
                    .map((category, index) => (
                      <p key={category.value}>
                        {index !== 0 ? <span className="mr-2">•</span> : ""}
                        {category.label}
                      </p>
                    ))}
                </div>
              </div>
              <div className="w-full flex flex-row items-start gap-x-2 mt-2">
                <div className="w-44 shrink-0 flex flex-row items-center gap-x-2">
                  <TicketIcon className="w-4 h-4" />
                  {translate("eventSetup.eventInfo.addEventInfo.minPrice")}:
                </div>
                <p>{formatNumber(getValues("event_info.min_price"))}</p>
              </div>
              <div className="w-full flex flex-row items-start gap-x-2 mt-2">
                <div className="w-44 shrink-0 flex flex-row items-center gap-x-2">
                  <TagIcon className="w-4 h-4" />
                  {translate("eventSetup.eventInfo.addEventInfo.eventTag")}:
                </div>
                <p>
                  {
                    SPECIAL_TYPES.find(
                      (type) =>
                        type.value === getValues("event_info.special_type")
                    )?.label
                  }
                </p>
              </div>
              <div className="w-full flex flex-row items-start gap-x-2 mt-2">
                <div className="w-44 shrink-0 flex flex-row items-center gap-x-2">
                  <TicketIcon className="w-4 h-4" />
                  {translate("eventSetup.eventInfo.addEventInfo.totalTicketForSale")}:
                </div>
                <p>{formatNumber(getValues("event_info.selling_max_qty"))}</p>
              </div>
            </div>
          </div>
        )
      ) : (
        <div className="grid gap-x-4 gap-y-6 sm:grid-cols-6 lg:gap-x-6">
          <div className="col-span-full">
            <div>
              <Label
                id="event_info.banner_url"
                name={translate(
                  "eventSetup.eventInfo.addEventInfo.eventBanner"
                )}
              />
              <Controller
                control={control}
                name={`event_info.banner_url`}
                render={({ field: { onChange, value = "" } }) => {
                  return (
                    <Dropzone
                      id={`event_info.banner_url`}
                      validator={{
                        width: 1440,
                        height: 600,
                      }}
                      onChange={(imageUrl: string) => onChange(imageUrl)}
                      value={value}
                    />
                  );
                }}
              />
              <p className="mt-1 text-sm text-[#868E95]">
                {translate("dropzone.recommendImageSize")}: 1440 x 600px{" "}
                <span className="mx-2">•</span>{" "}
                {translate("dropzone.maximumFileSize")} size: 2MB{" "}
                <span className="mx-2">•</span>{" "}
                {translate("dropzone.supportedImageFiles")}: PNG, JPEG, JPG
              </p>
            </div>
            {get(errors, `event_info.banner_url`, "") ? (
              <p className="error-text">
                <span className="font-medium">
                  {get(errors, `event_info.banner_url.message`, "")}
                </span>
              </p>
            ) : null}
          </div>
          <div className="sm:col-span-3">
            <Label
              required
              id="event_info.timezone"
              name={translate("eventSetup.eventInfo.addEventInfo.timezone")}
            />
            <Controller
              control={control}
              name={`event_info.timezone`}
              render={({ field: { onChange, value = "", ref } }) => {
                return (
                  <Select
                    key={`timezone`}
                    inputRef={ref}
                    options={timezoneArr}
                    id={`event_info.timezone`}
                    placeholder="Select Timezone"
                    isDisabled={true}
                    defaultValue={
                      timezoneArr.find(
                        (selected: { label: string; value: any }) =>
                          selected.value === value
                      ) ?? ""
                    }
                    value={timezoneArr.find(
                      (selected: { label: string; value: any }) =>
                        selected.value === value
                    )}
                    onChange={(selected: { label: string; value: any }) => {
                      onChange(selected?.value);
                    }}
                    error={get(errors, `event_info.timezone`, "")}
                  />
                );
              }}
            />
            {get(errors, `event_info.timezone`, "") ? (
              <p className="error-text">
                <span className="font-medium">
                  {get(errors, `event_info.timezone.message`, "")}
                </span>
              </p>
            ) : null}
          </div>
          <div className="sm:col-span-3">
            <Label
              required
              id="event_info.currency"
              name={translate(
                "eventSetup.eventInfo.addEventInfo.eventCurrency"
              )}
            />
            <Controller
              control={control}
              name={`event_info.currency`}
              render={({ field: { onChange, value = "", ref } }) => {
                return (
                  <Select
                    key={`currency`}
                    inputRef={ref}
                    options={CURRENCIES}
                    id={`event_info.currency`}
                    placeholder="Select Currency"
                    isDisabled={
                      canAccessByType(UserType.ADMIN)
                        ? isOpenRegistration(
                            eventDetailData?.open_registration_date
                          )
                        : true
                    }
                    defaultValue={
                      CURRENCIES.find(
                        (selected: { label: string; value: string }) =>
                          selected.value === value
                      ) ?? ""
                    }
                    value={CURRENCIES.find(
                      (selected: { label: string; value: string }) =>
                        selected.value === value
                    )}
                    onChange={(selected: { label: string; value: string }) => {
                      onChange(selected?.value);
                    }}
                    error={get(errors, `event_info.currency`, "")}
                  />
                );
              }}
            />
            {get(errors, `event_info.currency`, "") ? (
              <p className="error-text">
                <span className="font-medium">
                  {get(errors, `event_info.currency.message`, "")}
                </span>
              </p>
            ) : null}
          </div>
          <div className="sm:col-span-3">
            <Label
              required
              id="event_info.start_date"
              name={translate(
                "eventSetup.eventInfo.addEventInfo.eventStartDate"
              )}
            />
            <Controller
              control={control}
              name="event_info.start_date"
              render={({ field: { onChange, value = "" } }) => {
                return (
                  <DateTimePicker
                    id="event_info.start_date"
                    placeholder={translate(
                      "eventSetup.eventInfo.addEventInfo.eventStartDate"
                    )}
                    className="bg-white"
                    error={get(errors, `event_info.start_date.message`, "")}
                    value={value}
                    onChange={(date: any) =>
                      date === null
                        ? onChange("")
                        : onChange(moment(date).format("YYYY-MM-DD HH:mm:ss"))
                    }
                  />
                );
              }}
            />
            {get(errors, `event_info.start_date`, "") ? (
              <p className="error-text">
                <span className="font-medium">
                  {get(errors, `event_info.start_date.message`, "")}
                </span>
              </p>
            ) : null}
          </div>
          <div className="sm:col-span-3">
            <Label
              required
              id="event_info.end_date"
              name={translate("eventSetup.eventInfo.addEventInfo.eventEndDate")}
            />
            <Controller
              control={control}
              name="event_info.end_date"
              render={({ field: { onChange, value = "" } }) => {
                return (
                  <DateTimePicker
                    id="event_info.end_date"
                    placeholder={translate(
                      "eventSetup.eventInfo.addEventInfo.eventEndDate"
                    )}
                    className="bg-white"
                    error={get(errors, `event_info.end_date.message`, "")}
                    value={value}
                    onChange={(date: any) =>
                      date === null
                        ? onChange("")
                        : onChange(moment(date).format("YYYY-MM-DD HH:mm:ss"))
                    }
                  />
                );
              }}
            />
            {get(errors, `event_info.end_date`, "") ? (
              <p className="error-text">
                <span className="font-medium">
                  {get(errors, `event_info.end_date.message`, "")}
                </span>
              </p>
            ) : null}
          </div>
          <div className="sm:col-span-3">
            <Label
              required
              id="event_info.country"
              name={translate("eventSetup.eventInfo.addEventInfo.eventCountry")}
            />
            <Controller
              control={control}
              name={`event_info.country`}
              render={({ field: { onChange, value = "", ref } }) => {
                return (
                  <Select
                    key={`country`}
                    inputRef={ref}
                    options={COUNTRY_OPTIONS}
                    id={`event_info.country`}
                    placeholder={translate(
                      "eventSetup.eventInfo.addEventInfo.eventCountry"
                    )}
                    defaultValue={
                      COUNTRY_OPTIONS.find(
                        (selected: { label: string; value: string }) =>
                          selected.value === value
                      ) ?? ""
                    }
                    value={COUNTRY_OPTIONS.find(
                      (selected: { label: string; value: string }) =>
                        selected.value === value
                    )}
                    onChange={(selected: { label: string; value: string }) => {
                      onChange(selected?.value);
                      if (selected?.value !== "Vietnam") {
                        setValue("event_info.venue", "", {
                          shouldValidate: true,
                        });
                      }
                    }}
                    error={get(errors, `event_info.country`, "")}
                  />
                );
              }}
            />
            {get(errors, `event_info.country`, "") ? (
              <p className="error-text">
                <span className="font-medium">
                  {get(errors, `event_info.country.message`, "")}
                </span>
              </p>
            ) : null}
          </div>
          <div className="sm:col-span-3">
            <Label
              required
              id="event_info.venue"
              name={translate("eventSetup.eventInfo.addEventInfo.eventVenue")}
            />
            <Controller
              control={control}
              name={`event_info.venue`}
              render={({ field: { onChange, value = "", ref } }) => {
                return (
                  <Select
                    key={`venue__${watch("event_info.country")}`}
                    inputRef={ref}
                    options={
                      watch("event_info.country") === "Vietnam"
                        ? provinceIds
                        : []
                    }
                    id={`event_info.venue`}
                    placeholder={translate(
                      "eventSetup.eventInfo.addEventInfo.eventVenue"
                    )}
                    defaultValue={
                      provinceIds.find(
                        (selected: { label: string; value: string }) =>
                          selected.value === value
                      ) ?? ""
                    }
                    value={provinceIds.find(
                      (selected: { label: string; value: string }) =>
                        selected.value === value
                    )}
                    onChange={(selected: { label: string; value: string }) => {
                      onChange(selected?.value);
                    }}
                    error={get(errors, `event_info.venue`, "")}
                  />
                );
              }}
            />
            {get(errors, `event_info.venue`, "") ? (
              <p className="error-text">
                <span className="font-medium">
                  {get(errors, `event_info.venue.message`, "")}
                </span>
              </p>
            ) : null}
          </div>
          <div className="sm:col-span-3">
            <Label
              required
              id="event_info.event_slug"
              name={translate("eventSetup.eventInfo.addEventInfo.eventLink")}
              hint={translate(
                "eventSetup.eventInfo.addEventInfo.eventLinkNote"
              )}
            />
            <div className="flex">
              <span className="inline-flex items-center rounded-l-md border border-r-0 border-gray-300 bg-gray-100 px-3 text-gray-500 sm:text-sm">
                {CONFIG.DOMAIN}/event/
              </span>
              <div className="relative flex-1">
                <Input
                  id="event_info.event_slug"
                  className="rounded-l-none pr-10"
                  placeholder="slug-name"
                  maxLength={100}
                  error={errors["event_info"]?.event_slug}
                  {...register("event_info.event_slug")}
                />
                {_renderGenerateSlugButton()}
              </div>
            </div>
            {get(errors, `event_info.event_slug`, "") ? (
              <p className="error-text">
                <span className="font-medium">
                  {get(errors, `event_info.event_slug.message`, "")}
                </span>
              </p>
            ) : null}
          </div>
          <div className="sm:col-span-3">
            <Label
              id="categories"
              name={translate(
                "eventSetup.eventInfo.addEventInfo.eventCategories"
              )}
              hint={translate(
                "eventSetup.eventInfo.addEventInfo.eventCategoriesNote"
              )}
            />
            <Controller
              control={control}
              name="event_info.event_category_ids"
              defaultValue={[]}
              render={({ field: { onChange, value = [], ref } }) => (
                <Select
                  isMulti
                  key={`categories`}
                  inputRef={ref}
                  options={categoryIds}
                  id="event_ids"
                  placeholder={translate(
                    "eventSetup.eventInfo.addEventInfo.eventCategories"
                  )}
                  value={value.map((id: string) => {
                    return categoryIds.find((item) => item.value === id);
                  })}
                  onChange={(selected: { label: string; value: string }[]) =>
                    onChange(selected.map((item) => `${item.value}`))
                  }
                  error={errors["event_info"]?.event_category_ids}
                  isLoading={isLoadingCategories}
                />
              )}
            />
            {errors["event_info"]?.event_category_ids ? (
              <p className="error-text">
                <span className="font-medium">
                  {errors["event_info"]?.event_category_ids.message}
                </span>
              </p>
            ) : null}
          </div>
          <div className="sm:col-span-3">
            <div className="flex items-center justify-start gap-x-4">
              <Label
                id="event_info.min_price"
                name={translate("eventSetup.eventInfo.addEventInfo.minPrice")}
                required
              />
              <div className="mb-2">
                <Controller
                  control={control}
                  name="event_info.price_type"
                  render={({ field: { onChange, value = "", ref } }) => {
                    return (
                      <div className="flex flex-row flex-wrap gap-x-3">
                        {PRICE_TYPES.map((type) => (
                          <RadioButton
                            key={type.value}
                            checked={type.value === value}
                            onChange={() => {
                              onChange(type.value);
                              if (type.value === PriceType.FREE) {
                                setValue("event_info.min_price", 0, {
                                  shouldValidate: true,
                                });
                              }
                            }}
                            item={{
                              id: type.value,
                              title: type.label,
                            }}
                          />
                        ))}
                      </div>
                    );
                  }}
                />
                {get(errors, `event_info.price_type`, "") ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {get(errors, `event_info.price_type.message`, "")}
                    </span>
                  </p>
                ) : null}
              </div>
            </div>
            <Input
              id={`event_info.min_price`}
              type="number"
              error={get(errors, `event_info.min_price`, "")}
              placeholder={translate(
                "eventSetup.eventInfo.addEventInfo.minPrice"
              )}
              min={0}
              disabled={watch("event_info.price_type") === PriceType.FREE}
              {...register("event_info.min_price", {
                valueAsNumber: true,
              })}
            />
            {get(errors, `event_info.min_price`, "") ? (
              <p className="error-text">
                <span className="font-medium">
                  {get(errors, `event_info.min_price.message`, "")}
                </span>
              </p>
            ) : null}
          </div>
          <div className="sm:col-span-3">
            <Label
              id="event_info.special_type"
              name={translate("eventSetup.eventInfo.addEventInfo.eventTag")}
            />
            <Controller
              control={control}
              name={`event_info.special_type`}
              render={({ field: { onChange, value = "", ref } }) => {
                return (
                  <Select
                    key={`special_type`}
                    inputRef={ref}
                    options={SPECIAL_TYPES}
                    id={`event_info.special_type`}
                    placeholder={translate(
                      "eventSetup.eventInfo.addEventInfo.eventTag"
                    )}
                    isClearable={true}
                    defaultValue={
                      SPECIAL_TYPES.find(
                        (selected: { label: string; value: string }) =>
                          selected.value === value
                      ) ?? ""
                    }
                    value={SPECIAL_TYPES.find(
                      (selected: { label: string; value: string }) =>
                        selected.value === value
                    )}
                    onChange={(
                      selected: { label: string; value: string },
                      { action }: any
                    ) => {
                      if (action === "clear") {
                        onChange("");
                      } else {
                        onChange(selected?.value);
                      }
                    }}
                    error={get(errors, `event_info.special_type`, "")}
                  />
                );
              }}
            />
            {get(errors, `event_info.special_type`, "") ? (
              <p className="error-text">
                <span className="font-medium">
                  {get(errors, `event_info.special_type.message`, "")}
                </span>
              </p>
            ) : null}
          </div>
          <div className="sm:col-span-3">
            <Label
              id="event_info.selling_max_qty"
              name={translate(
                "eventSetup.eventInfo.addEventInfo.totalTicketForSale"
              )}
              required
            />
            <Input
              id={`event_info.selling_max_qty`}
              type="number"
              error={get(errors, `event_info.selling_max_qty`, "")}
              placeholder={translate(
                "eventSetup.eventInfo.addEventInfo.totalTicketForSale"
              )}
              min={0}
              {...register("event_info.selling_max_qty", {
                valueAsNumber: true,
              })}
            />
            {get(errors, `event_info.selling_max_qty`, "") ? (
              <p className="error-text">
                <span className="font-medium">
                  {get(errors, `event_info.selling_max_qty.message`, "")}
                </span>
              </p>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};
